(() => {
  const CORE_VERSION = '3.1.2';
  if (!('customElements' in window)) return;
  if (document.documentElement.dataset.avPdpCoreVersion === CORE_VERSION) return;
  document.documentElement.dataset.avPdpCoreVersion = CORE_VERSION;

  const track = (name, payload = {}, options = {}) => window.AvonticAnalytics?.track?.(name, payload, options) === true;
  const readConfig = (root, selector) => {
    try {
      return JSON.parse(root.querySelector(selector)?.textContent || '{}');
    } catch (_) {
      return {};
    }
  };
  const observeView = (element, callback) => {
    if (!('IntersectionObserver' in window)) {
      callback();
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= .35)) return;
      observer.disconnect();
      callback();
    }, { threshold: [.35] });
    observer.observe(element);
  };
  const replaceOption = (template, option) => String(template || '').replace('__OPTION__', option);
  const normalizeKey = (value) => String(value || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[\u2018\u2019]/g, "'")
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
  const normalizeModelNumber = (value) => String(value || '')
    .toUpperCase()
    .replace(/[\s\-–—]/g, '');
  const finderState = window.AvonticPdpFinderState instanceof Map
    ? window.AvonticPdpFinderState
    : new Map();
  window.AvonticPdpFinderState = finderState;
  const confirmationState = window.AvonticPdpConfirmationState instanceof Map
    ? window.AvonticPdpConfirmationState
    : new Map();
  window.AvonticPdpConfirmationState = confirmationState;

  class AvonticPdpValueSummary extends HTMLElement {
    connectedCallback() {
      observeView(this, () => track('av_pdp_value_summary_view', {
        component: 'pdp_value_summary',
        product_id: this.dataset.productId
      }, { oncePerSession: true, dedupeKey: this.dataset.productId || 'value_summary' }));
    }
  }

  class AvonticPdpModelFinder extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.config = readConfig(this, '[data-av-pdp-model-config]');
      this.stateKey = String(this.config.productId || this.dataset.productId || '');
      this.details = this.querySelector('details');
      this.input = this.querySelector('[data-av-pdp-model-input]');
      this.listbox = this.querySelector('[data-av-pdp-model-listbox]');
      this.submitButton = this.querySelector('[data-av-pdp-model-submit]');
      this.result = this.querySelector('[data-av-pdp-model-result]');
      this.choices = this.querySelector('[data-av-pdp-model-choices]');
      this.contact = this.querySelector('[data-av-pdp-model-contact]');
      this.modelOptions = this.buildModelOptions();
      this.activeModelIndex = -1;
      this.details?.addEventListener('toggle', () => {
        if (!this.details.open) return;
        track('av_model_finder_open', {
          component: 'pdp_model_finder',
          product_id: this.config.productId
        }, { oncePerSession: true, dedupeKey: this.config.productId || 'model_finder' });
      }, { signal: this.controller.signal });
      this.submitButton?.addEventListener('click', () => this.submit(), { signal: this.controller.signal });
      this.input?.addEventListener('focus', () => this.openModelList(), { signal: this.controller.signal });
      this.input?.addEventListener('click', () => this.openModelList(), { signal: this.controller.signal });
      this.input?.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          this.moveActiveModel(event.key === 'ArrowDown' ? 1 : -1);
          return;
        }
        if (event.key === 'Escape') {
          this.closeModelList();
          return;
        }
        if (event.key !== 'Enter') return;
        event.preventDefault();
        const activeOption = this.visibleModelOptions[this.activeModelIndex];
        if (!this.listbox?.hidden && activeOption) this.selectModelOption(activeOption);
        else this.submit();
      }, { signal: this.controller.signal });
      this.input?.addEventListener('input', () => {
        finderState.delete(this.stateKey);
        this.emitVerification(false);
        this.resetResult();
        this.openModelList(this.input.value);
      }, { signal: this.controller.signal });
      document.addEventListener('pointerdown', (event) => {
        if (!this.contains(event.target)) this.closeModelList();
      }, { signal: this.controller.signal });
      this.addEventListener('focusout', () => {
        window.setTimeout(() => {
          if (!this.contains(document.activeElement)) this.closeModelList();
        }, 0);
      }, { signal: this.controller.signal });
      this.contact?.addEventListener('click', () => {
        track('av_model_finder_support_click', {
          component: 'pdp_model_finder',
          product_id: this.config.productId,
          result: 'support_requested'
        });
      }, { signal: this.controller.signal });
      this.renderModelOptions();
      queueMicrotask(() => {
        if (!this.submitUrlModel()) this.restoreState();
      });
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    resetResult() {
      if (this.result) {
        this.result.replaceChildren();
        this.result.removeAttribute('data-state');
        this.result.hidden = true;
      }
      if (this.choices) {
        this.choices.replaceChildren();
        this.choices.hidden = true;
      }
      if (this.contact) this.contact.hidden = true;
    }

    setResult(message, state) {
      if (!this.result) return;
      this.result.replaceChildren();
      const status = document.createElement('p');
      status.className = 'av-pdp-finder__status';
      status.textContent = message || '';
      this.result.append(status);
      this.result.hidden = false;
      if (state) this.result.dataset.state = state;
      else this.result.removeAttribute('data-state');
    }

    values(entry, ...keys) {
      const value = keys.map((key) => entry?.[key]).find((candidate) => candidate !== undefined && candidate !== null);
      if (Array.isArray(value)) return value.map(String).map((item) => item.trim()).filter(Boolean);
      if (value && typeof value === 'object') return Object.values(value).map(String).map((item) => item.trim()).filter(Boolean);
      return String(value || '').split(',').map((item) => item.trim()).filter(Boolean);
    }

    isApprovedOptionKey(key) {
      const blocked = new Set((this.config.blockedOptionKeys || []).map((value) => normalizeKey(value)));
      return !blocked.has(normalizeKey(key));
    }

    buildModelOptions() {
      const optionValues = new Map();
      (this.config.optionValues || []).forEach((option) => {
        optionValues.set(String(option.label), option);
        optionValues.set(normalizeKey(option.label), option);
      });
      const seen = new Set();
      return Object.entries(this.config.compatibilityMap || {}).flatMap(([key, entry]) => {
        if (!this.isApprovedOptionKey(key)) return [];
        const option = optionValues.get(key) || optionValues.get(normalizeKey(key));
        if (!option?.available) return [];
        return this.values(entry, 'model_numbers', 'modelNumbers').flatMap((number) => {
          const modelNumber = normalizeModelNumber(number);
          const rowKey = `${modelNumber}:${option.id}`;
          if (!/^A\d{4}$/.test(modelNumber) || seen.has(rowKey)) return [];
          seen.add(rowKey);
          return [{ modelNumber, key, option }];
        });
      }).sort((first, second) => (
        first.modelNumber.localeCompare(second.modelNumber, undefined, { numeric: true })
        || first.key.localeCompare(second.key)
      ));
    }

    renderModelOptions(query = '') {
      if (!this.listbox) return;
      const modelQuery = normalizeModelNumber(query);
      const labelQuery = normalizeKey(query);
      this.visibleModelOptions = this.modelOptions.filter((entry) => (
        !query
        || entry.modelNumber.includes(modelQuery)
        || normalizeKey(entry.key).includes(labelQuery)
      ));
      this.listbox.replaceChildren();
      this.visibleModelOptions.forEach((entry, index) => {
        const option = document.createElement('div');
        option.id = `${this.listbox.id}-option-${index}`;
        option.className = 'av-pdp-finder__model-option';
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', 'false');
        option.dataset.modelNumber = entry.modelNumber;
        option.dataset.optionKey = entry.key;
        option.tabIndex = -1;
        const model = document.createElement('strong');
        model.textContent = entry.modelNumber;
        const fit = document.createElement('span');
        fit.textContent = entry.key;
        option.append(model, fit);
        option.addEventListener('pointerdown', (event) => event.preventDefault(), { signal: this.controller.signal });
        option.addEventListener('click', () => this.selectModelOption(entry), { signal: this.controller.signal });
        this.listbox.append(option);
      });
      this.activeModelIndex = -1;
      this.input?.removeAttribute('aria-activedescendant');
    }

    openModelList(query = '') {
      if (!this.listbox || !this.input) return;
      this.renderModelOptions(query);
      const hasOptions = this.visibleModelOptions.length > 0;
      this.listbox.hidden = !hasOptions;
      this.input.setAttribute('aria-expanded', hasOptions ? 'true' : 'false');
    }

    closeModelList() {
      if (this.listbox) this.listbox.hidden = true;
      if (this.input) {
        this.input.setAttribute('aria-expanded', 'false');
        this.input.removeAttribute('aria-activedescendant');
      }
      this.activeModelIndex = -1;
    }

    moveActiveModel(direction) {
      if (this.listbox?.hidden) this.openModelList(this.input?.value || '');
      if (!this.visibleModelOptions?.length || !this.listbox) return;
      this.activeModelIndex = (this.activeModelIndex + direction + this.visibleModelOptions.length) % this.visibleModelOptions.length;
      const options = Array.from(this.listbox.querySelectorAll('[role="option"]'));
      options.forEach((option, index) => option.setAttribute('aria-selected', index === this.activeModelIndex ? 'true' : 'false'));
      const activeOption = options[this.activeModelIndex];
      if (!activeOption) return;
      this.input?.setAttribute('aria-activedescendant', activeOption.id);
      activeOption.scrollIntoView({ block: 'nearest' });
    }

    selectModelOption(entry) {
      if (!entry || !this.input) return;
      this.input.value = entry.modelNumber;
      this.closeModelList();
      this.submit(entry.key);
    }

    exactMatches(modelNumber) {
      const optionValues = new Map();
      (this.config.optionValues || []).forEach((option) => {
        optionValues.set(String(option.label), option);
        optionValues.set(normalizeKey(option.label), option);
      });
      return Object.entries(this.config.compatibilityMap || {}).flatMap(([key, entry]) => {
        if (!this.isApprovedOptionKey(key)) return [];
        const numbers = this.values(entry, 'model_numbers', 'modelNumbers');
        const matched = numbers.some((number) => normalizeModelNumber(number) === modelNumber);
        const option = optionValues.get(key) || optionValues.get(normalizeKey(key));
        return matched && option ? [{
          key,
          option,
          modelNumbers: numbers,
          years: this.values(entry, 'years', 'model_years', 'modelYears')
        }] : [];
      });
    }

    appendFact(list, label, values) {
      if (!values?.length) return;
      const wrapper = document.createElement('div');
      const term = document.createElement('dt');
      const description = document.createElement('dd');
      term.textContent = label || '';
      description.textContent = values.join(' · ');
      wrapper.append(term, description);
      list.append(wrapper);
    }

    createMatchCard(match, actionLabel) {
      const card = document.createElement('article');
      card.className = 'av-pdp-finder__match-card';
      const title = document.createElement('strong');
      title.className = 'av-pdp-finder__match-title';
      title.textContent = match.key;
      const facts = document.createElement('dl');
      facts.className = 'av-pdp-finder__match-facts';
      this.appendFact(facts, this.config.translations?.years, match.years);
      this.appendFact(facts, this.config.translations?.models, match.modelNumbers);
      this.appendFact(facts, this.config.translations?.availability, [
        match.option.available ? this.config.translations?.available : this.config.translations?.unavailable
      ]);
      const authority = document.createElement('p');
      authority.className = 'av-pdp-finder__authority';
      authority.textContent = this.config.translations?.authority || '';
      card.append(title, facts, authority);
      if (actionLabel && match.option.available) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'av-pdp-finder__choice';
        button.textContent = actionLabel;
        button.addEventListener('click', () => this.selectOption(match), { signal: this.controller.signal });
        card.append(button);
      }
      return card;
    }

    renderMatch(match, message, state, actionLabel) {
      this.setResult(message, state);
      this.result?.append(this.createMatchCard(match, actionLabel));
      if (this.details) this.details.open = true;
    }

    restoreState() {
      const saved = finderState.get(this.stateKey);
      if (!saved?.modelNumber) return;
      const match = this.exactMatches(saved.modelNumber).find((candidate) => candidate.key === saved.optionKey);
      if (!match) {
        finderState.delete(this.stateKey);
        return;
      }
      const form = document.getElementById(this.config.formId) || document.forms[this.config.formId];
      const selected = Array.from(form?.elements || []).find((element) => (
        element.matches?.(`input[data-option-position="${this.config.optionPosition}"]:checked`)
      ));
      if (String(selected?.value || '') !== String(match.option.id)) {
        finderState.delete(this.stateKey);
        return;
      }
      this.lastModelNumber = saved.modelNumber;
      if (this.input) this.input.value = saved.modelNumber;
      this.renderMatch(
        match,
        replaceOption(this.config.translations?.applied, match.key),
        match.option.available ? 'matched' : 'unavailable'
      );
      this.emitVerification(match.option.available, match);
      if (this.details) this.details.open = true;
    }

    submitUrlModel() {
      const modelNumber = normalizeModelNumber(new URLSearchParams(window.location.search).get('model'));
      if (!/^A\d{4}$/.test(modelNumber) || !this.input) return false;
      this.input.value = modelNumber;
      if (this.details) this.details.open = true;
      this.submit();
      return true;
    }

    rememberMatch(match) {
      if (!this.stateKey || !this.lastModelNumber) return;
      finderState.set(this.stateKey, {
        modelNumber: this.lastModelNumber,
        optionKey: match.key
      });
    }

    emitVerification(verified, match = null) {
      document.dispatchEvent(new CustomEvent('avontic:pdp-fit-verification', {
        detail: {
          productId: String(this.config.productId || ''),
          verified: Boolean(verified),
          modelNumber: verified ? this.lastModelNumber : '',
          optionKey: verified ? String(match?.key || '') : ''
        }
      }));
    }

    async submit(preferredKey = '') {
      const modelNumber = normalizeModelNumber(this.input?.value);
      this.lastModelNumber = modelNumber;
      this.closeModelList();
      this.resetResult();
      track('av_model_finder_submit', {
        component: 'pdp_model_finder',
        product_id: this.config.productId,
        result: 'attempted'
      });
      if (!/^A\d{4}$/.test(modelNumber)) {
        this.setResult(this.config.translations?.invalid, 'error');
        return;
      }
      let matches = this.exactMatches(modelNumber);
      const preferredMatch = matches.find((match) => match.key === preferredKey);
      if (preferredMatch) matches = [preferredMatch];
      if (matches.length === 1) {
        const match = matches[0];
        if (match.option.available) {
          await this.selectOption(match);
        } else {
          this.renderMatch(
            match,
            replaceOption(this.config.translations?.match, match.key),
            'unavailable'
          );
        }
        track('av_model_finder_match', {
          component: 'pdp_model_finder',
          product_id: this.config.productId,
          result: 'exact',
          result_count: 1
        });
        return;
      }
      if (matches.length > 1) {
        this.setResult(this.config.translations?.multiple, 'multiple');
        track('av_model_finder_multiple', {
          component: 'pdp_model_finder',
          product_id: this.config.productId,
          result: 'choice_required',
          result_count: matches.length
        });
        if (this.choices) this.choices.hidden = false;
        matches.forEach((match) => {
          this.choices?.append(this.createMatchCard(match, replaceOption(this.config.translations?.choose, match.key)));
        });
        return;
      }
      this.setResult(this.config.translations?.notFound, 'not-found');
      if (this.contact) this.contact.hidden = false;
      track('av_model_finder_not_found', {
        component: 'pdp_model_finder',
        product_id: this.config.productId,
        result: 'not_found',
        result_count: 0
      });
    }

    async selectOption(match) {
      const form = document.getElementById(this.config.formId) || document.forms[this.config.formId];
      const productRoot = this.closest('product-rerender, .product, #avontic-primary-product') || document;
      const picker = productRoot.querySelector(`variant-picker[form-id="${this.config.formId}"]`)
        || document.querySelector(`variant-picker[form-id="${this.config.formId}"]`);
      if (!form) {
        this.setResult(this.config.translations?.error, 'error');
        return;
      }
      const currentOption = Array.from(form.elements).find((element) => (
        element.matches?.(`input[data-option-position="${this.config.optionPosition}"]:checked`)
      ));
      if (String(currentOption?.value || '') === String(match.option.id)) {
        this.rememberMatch(match);
        this.renderMatch(match, replaceOption(this.config.translations?.applied, match.key), match.option.available ? 'matched' : 'unavailable');
        this.emitVerification(match.option.available, match);
        track('av_model_finder_option_applied', {
          component: 'pdp_model_finder',
          product_id: this.config.productId,
          result: 'already_selected'
        });
        return;
      }
      if (!picker) {
        this.setResult(this.config.translations?.error, 'error');
        return;
      }
      const checked = Array.from(form.elements)
        .filter((element) => element.matches?.('input[data-option-position]:checked'))
        .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition));
      const optionValues = checked.map((element) => element.value);
      optionValues[Number(this.config.optionIndex)] = String(match.option.id);
      try {
        this.rememberMatch(match);
        form.dispatchEvent(new CustomEvent('avontic:compatibility-changing', {
          bubbles: true,
          detail: { formId: this.config.formId }
        }));
        if (typeof picker.selectCombination === 'function' && optionValues.filter(Boolean).length === checked.length) {
          await picker.selectCombination({ optionValues, productChange: false });
        } else {
          const candidate = Array.from(form.elements).find((element) => element.matches?.(`input[data-option-position="${this.config.optionPosition}"]`) && String(element.value) === String(match.option.id));
          if (!candidate) throw new Error('missing option');
          candidate.checked = true;
          candidate.dispatchEvent(new Event('change', { bubbles: true }));
        }
        this.renderMatch(match, replaceOption(this.config.translations?.applied, match.key), match.option.available ? 'matched' : 'unavailable');
        this.emitVerification(match.option.available, match);
        if (this.choices) {
          this.choices.replaceChildren();
          this.choices.hidden = true;
        }
        track('av_model_finder_option_applied', {
          component: 'pdp_model_finder',
          product_id: this.config.productId,
          result: 'selected'
        });
      } catch (_) {
        finderState.delete(this.stateKey);
        this.emitVerification(false);
        this.setResult(this.config.translations?.error, 'error');
      }
    }
  }

  class AvonticPdpSelectionSummary extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.config = readConfig(this, '[data-av-pdp-selection-config]');
      this.stateElement = this.querySelector('[data-av-pdp-state]');
      this.guidanceElement = this.querySelector('[data-av-pdp-guidance]');
      this.confirmButton = this.querySelector('[data-av-confirm-selection]');
      this.confirmButtonLabel = this.querySelector('[data-av-confirm-selection-label]');
      this.stateKey = String(this.config.productId || this.dataset.productId || '');
      const productRoot = this.closest('#avontic-primary-product') || document.querySelector('#avontic-primary-product') || document;
      const summarySelector = `avontic-pdp-selection-summary[data-product-id="${CSS.escape(this.stateKey)}"]`;
      const summaryPeers = [...productRoot.querySelectorAll(summarySelector)];
      if (this.dataset.blockId) {
        summaryPeers.forEach((peer) => {
          if (peer !== this && !peer.dataset.blockId) {
            peer.hidden = true;
            peer.controller?.abort();
          }
        });
      } else if (summaryPeers.some((peer) => peer !== this && peer.dataset.blockId)) {
        this.hidden = true;
        return;
      }
      this.hidden = false;
      const savedMatch = finderState.get(String(this.config.productId || ''));
      if (savedMatch?.optionKey && String(savedMatch.optionKey) === String(this.config.essentialValue || '')) {
        this.rememberConfirmation('model-finder');
      }
      this.syncConfirmation(this.restoreConfirmation());
      observeView(this, () => track('av_pdp_selection_summary_view', {
        component: 'pdp_selection_summary',
        product_id: this.config.productId,
        variant_id: this.config.variantId,
        compatibility_state: this.dataset.state || this.config.displayState
      }, { oncePerSession: true, dedupeKey: this.config.productId || 'selection_summary' }));
      const complete = !this.config.confirmationRequired || this.restoreConfirmation();
      track(complete ? 'av_pdp_configuration_complete' : 'av_pdp_configuration_incomplete', {
        component: 'pdp_selection_summary',
        product_id: this.config.productId,
        variant_id: this.config.variantId,
        compatibility_state: this.dataset.state || this.config.displayState,
        result: complete ? 'complete' : 'incomplete'
      }, { dedupeKey: `${this.config.productId}:${this.config.variantId}:${this.dataset.state || this.config.displayState}` });
      this.confirmButton?.addEventListener('click', () => {
        if (this.restoreConfirmation()) {
          this.clearConfirmation();
          this.syncConfirmation(false);
          this.focusConfigurator();
          return;
        }
        this.rememberConfirmation('confirmation-control');
        this.syncConfirmation(true);
        track('av_pdp_selection_confirmed', {
          component: 'pdp_selection_summary',
          product_id: this.config.productId,
          variant_id: this.config.variantId,
          selection: this.config.essentialValue || '',
          result: 'confirmed'
        });
      }, { signal: this.controller.signal });
      document.addEventListener('avontic:pdp-fit-verification', (event) => {
        if (String(event.detail?.productId || '') !== String(this.config.productId || '')) return;
        const exactSelection = String(event.detail?.optionKey || '') === String(this.config.essentialValue || '');
        if (event.detail?.verified && exactSelection) this.rememberConfirmation('model-finder');
        else this.clearConfirmation();
        this.syncConfirmation(Boolean(event.detail?.verified && exactSelection));
      }, { signal: this.controller.signal });
      document.addEventListener('change', (event) => {
        const control = event.target instanceof Element ? event.target.closest('[data-option-position]') : null;
        const productRoot = this.closest('#avontic-primary-product') || document.querySelector('#avontic-primary-product');
        const explicitOption = control?.closest('[data-av-explicit-selection-option="true"]');
        if (control && explicitOption && productRoot?.contains(control)) {
          this.clearConfirmation();
          this.syncConfirmation(false);
        }
      }, { capture: true, signal: this.controller.signal });
      document.addEventListener('variant:change', (event) => {
        const productRoot = this.closest('#avontic-primary-product') || document.querySelector('#avontic-primary-product');
        if (event.target instanceof Element && productRoot && !productRoot.contains(event.target)) return;
        const variantId = String(event.detail?.variant?.id || '');
        if (!/^\d+$/.test(variantId)) return;
        this.clearConfirmation();
        this.syncConfirmation(false);
        this.refreshForVariant(variantId);
      }, { signal: this.controller.signal });
      window.addEventListener('popstate', () => this.refreshFromUrl(), { signal: this.controller.signal });
    }

    storageKey() {
      return `avontic:selection-confirmation:${this.stateKey}`;
    }

    restoreConfirmation() {
      if (!this.config?.confirmationRequired) return true;
      let record = confirmationState.get(this.stateKey);
      if (!record) {
        try {
          record = JSON.parse(sessionStorage.getItem(this.storageKey()) || 'null');
        } catch (_) {
          record = null;
        }
      }
      return Boolean(record && String(record.essentialValue || '') === String(this.config.essentialValue || ''));
    }

    rememberConfirmation(source) {
      if (!this.config?.confirmationRequired || !this.stateKey) return;
      const record = {
        essentialValue: String(this.config.essentialValue || ''),
        source: String(source || 'confirmation-control')
      };
      confirmationState.set(this.stateKey, record);
      try {
        sessionStorage.setItem(this.storageKey(), JSON.stringify(record));
      } catch (_) {
        return;
      }
    }

    clearConfirmation() {
      confirmationState.delete(this.stateKey);
      try {
        sessionStorage.removeItem(this.storageKey());
      } catch (_) {
        return;
      }
    }

    syncConfirmation(confirmed) {
      if (!this.config?.confirmationRequired || !this.stateElement) return;
      const reviewRequired = this.config.gateState === 'review-required';
      const inventoryState = String(this.config.inventoryState || 'available');
      const inventoryUnavailable = inventoryState === 'unavailable';
      const inventoryDeferred = inventoryState === 'backorder' || inventoryState === 'preorder';
      const state = inventoryUnavailable
        ? 'unavailable'
        : inventoryDeferred
          ? 'backorder'
          : confirmed
            ? 'confirmed'
            : reviewRequired
              ? 'review-required'
              : 'confirmation-required';
      const label = inventoryUnavailable
        ? this.config.unavailableLabel
        : inventoryDeferred
          ? this.config.backorderLabel
          : confirmed
            ? this.config.confirmedLabel
            : reviewRequired
              ? this.config.reviewLabel
              : this.config.pendingLabel;
      this.dataset.state = state;
      this.stateElement.textContent = label || '';
      if (this.guidanceElement) {
        this.guidanceElement.textContent = confirmed
          ? this.config.confirmedGuidance || ''
          : this.config.pendingGuidance || '';
      }
      if (this.confirmButton) this.confirmButton.setAttribute('aria-pressed', confirmed ? 'true' : 'false');
      if (this.confirmButtonLabel) {
        this.confirmButtonLabel.textContent = confirmed
          ? this.config.changeActionLabel || ''
          : this.config.confirmActionLabel || '';
      }
      document.querySelectorAll(`.av-pdp-sticky-cockpit[data-product-id="${CSS.escape(String(this.config.productId || ''))}"]`).forEach((element) => {
        const stickyInventoryState = String(element.dataset.avInventoryState || 'available');
        const stickyInventoryIsAuthoritative = ['unavailable', 'backorder', 'preorder'].includes(stickyInventoryState);
        const stickyState = element.querySelector('.av-pdp-sticky__state');
        if (stickyState) {
          stickyState.textContent = stickyInventoryIsAuthoritative
            ? element.dataset.avInventoryLabel || label || ''
            : label || '';
        }
        element.dataset.avSelectionConfirmed = confirmed ? 'true' : 'false';
      });
      document.dispatchEvent(new CustomEvent('avontic:pdp-selection-confirmation', {
        detail: {
          productId: String(this.config.productId || ''),
          variantId: String(this.config.variantId || ''),
          confirmed: Boolean(confirmed),
          gateState: String(this.config.gateState || 'not-required')
        }
      }));
    }

    focusConfigurator() {
      const target = document.querySelector('#avontic-primary-product [data-av-explicit-selection-option="true"]')
        || document.querySelector('#avontic-primary-product variant-picker');
      target?.scrollIntoView({
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'center'
      });
      const control = target?.querySelector('button, input:not([type="hidden"]), select');
      window.setTimeout(() => control?.focus({ preventScroll: true }), 260);
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    refreshFromUrl() {
      return this.refreshForVariant(new URL(window.location.href).searchParams.get('variant'));
    }

    async refreshForVariant(variantId) {
      if (!variantId || !/^\d+$/.test(variantId)) return;
      const requestSequence = (this.refreshSequence || 0) + 1;
      this.refreshSequence = requestSequence;
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variantId);
        url.searchParams.set('section_id', this.dataset.sectionId);
        const response = await fetch(url, { headers: { Accept: 'text/html' }, signal: this.controller.signal });
        if (!response.ok) return;
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        const instanceId = this.dataset.instanceId;
        const replacement = Array.from(parsed.querySelectorAll('avontic-pdp-selection-summary')).find((element) => element.dataset.instanceId === instanceId);
        if (replacement && this.isConnected && requestSequence === this.refreshSequence) this.replaceWith(replacement.cloneNode(true));
      } catch (_) {
        return;
      }
    }
  }

  class AvonticPdpAnchorNavigation extends HTMLElement {
    connectedCallback() {
      const ready = () => {
        this.resolveLinks();
        this.linkObserver?.disconnect();
        this.linkObserver = new MutationObserver(() => {
          if (this.resolveFrame) return;
          this.resolveFrame = window.requestAnimationFrame(() => {
            this.resolveFrame = 0;
            this.resolveLinks();
          });
        });
        this.linkObserver.observe(document.querySelector('main') || document.body, { childList: true, subtree: true });
      };
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready, { once: true });
      else ready();
    }

    disconnectedCallback() {
      this.linkObserver?.disconnect();
      if (this.resolveFrame) window.cancelAnimationFrame(this.resolveFrame);
    }

    selectors() {
      return {
        configure: '#avontic-primary-product variant-picker, #avontic-primary-product',
        fit: 'avontic-pdp-model-finder, avontic-fit-check',
        protection: '[id^="RhinoGuardLab-"]',
        included: '[id^="RhinoGuardLab-"]',
        reviews: '#shopify-product-reviews, .jdgm-widget, .spr-container, [data-block-type="rating"]'
      };
    }

    targetFor(type) {
      const target = document.querySelector(this.selectors()[type]);
      if (type !== 'reviews' || !target) return target;
      const hasReviewContent = (target.textContent || '').trim().length > 0 ||
        target.getBoundingClientRect().height > 1 ||
        Boolean(target.querySelector('iframe, [data-review-id], .jdgm-rev-widg, .spr-review'));
      return hasReviewContent ? target : null;
    }

    resolveLinks() {
      this.querySelectorAll('[data-av-pdp-anchor]').forEach((link) => {
        const type = link.dataset.avPdpAnchor;
        const target = this.targetFor(type);
        link.hidden = !target;
        link.href = target?.id ? `#${target.id}` : '#';

        if (link.dataset.avPdpAnchorBound !== undefined) return;
        link.dataset.avPdpAnchorBound = '';
        link.addEventListener('click', (event) => {
          const currentType = link.dataset.avPdpAnchor;
          const currentTarget = this.targetFor(currentType);
          event.preventDefault();
          if (!currentTarget) {
            link.hidden = true;
            link.href = '#';
            return;
          }
          if (currentType === 'included') {
            document.querySelector('[id^="RhinoGuardLab-"][id$="-tab-system"], [id$="-tab-system"]')?.click();
          }
          currentTarget.scrollIntoView({
            behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
            block: 'start'
          });
        });
      });
    }
  }

  if (!customElements.get('avontic-pdp-value-summary')) customElements.define('avontic-pdp-value-summary', AvonticPdpValueSummary);
  if (!customElements.get('avontic-pdp-model-finder')) customElements.define('avontic-pdp-model-finder', AvonticPdpModelFinder);
  if (!customElements.get('avontic-pdp-selection-summary')) customElements.define('avontic-pdp-selection-summary', AvonticPdpSelectionSummary);
  if (!customElements.get('avontic-pdp-anchor-navigation')) customElements.define('avontic-pdp-anchor-navigation', AvonticPdpAnchorNavigation);

  const handledStickyPurchaseEvents = new WeakSet();
  const forwardStickyPurchase = (event) => {
    const control = event.target instanceof Element ? event.target.closest('.av-pdp-sticky-cockpit [data-av-atc-control]') : null;
    if (!control || handledStickyPurchaseEvents.has(event)) return false;
    handledStickyPurchaseEvents.add(event);
    const sticky = control.closest('.av-pdp-sticky-cockpit');
    const primaryControl = document.querySelector('#avontic-primary-product [data-av-atc-control]');
    const formId = control.getAttribute('form') || primaryControl?.getAttribute('form');
    const purchaseForm = (formId && document.getElementById(formId)) || primaryControl?.closest('form');
    track('av_sticky_configuration_click', {
      component: 'pdp_sticky_cockpit',
      product_id: sticky?.dataset.productId,
      variant_id: sticky?.dataset.variantId,
      compatibility_state: sticky?.dataset.avCompatibilityState,
      result: purchaseForm ? 'forwarded' : 'native_fallback'
    });
    if (!purchaseForm) return false;
    event.preventDefault();
    sticky.dataset.avAtcForwarded = 'true';
    if (typeof purchaseForm.requestSubmit === 'function') {
      purchaseForm.requestSubmit(primaryControl || control);
    } else {
      primaryControl?.click();
    }
    return true;
  };

  document.addEventListener('click', (event) => {
    const configureControl = event.target instanceof Element ? event.target.closest('[data-av-sticky-configure]') : null;
    if (configureControl) {
      const target = document.querySelector('#avontic-primary-product [data-av-explicit-selection-option="true"]')
        || document.querySelector('#avontic-primary-product variant-picker');
      target?.scrollIntoView({
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'center'
      });
      const targetControl = target?.querySelector('button, input:not([type="hidden"]), select');
      window.setTimeout(() => targetControl?.focus({ preventScroll: true }), 260);
      const sticky = configureControl.closest('.av-pdp-sticky-cockpit');
      track('av_sticky_configure_click', {
        component: 'pdp_sticky_cockpit',
        product_id: sticky?.dataset.productId,
        variant_id: sticky?.dataset.variantId,
        compatibility_state: sticky?.dataset.avCompatibilityState,
        result: target ? 'opened' : 'missing'
      });
      return;
    }
    forwardStickyPurchase(event);
  }, true);

  const bindStickyPurchaseControls = (root = document) => {
    const controls = new Set();
    if (root.matches?.('.av-pdp-sticky-cockpit [data-av-atc-control]')) controls.add(root);
    root.querySelectorAll?.('.av-pdp-sticky-cockpit [data-av-atc-control]').forEach((control) => controls.add(control));
    controls.forEach((control) => {
      if (control.dataset.avStickyPurchaseBound === 'true') return;
      control.dataset.avStickyPurchaseBound = 'true';
      control.addEventListener('click', forwardStickyPurchase, true);
    });
  };

  let stickyVisibilityFrame = 0;
  const syncStickyVisibility = () => {
    if (stickyVisibilityFrame) return;
    stickyVisibilityFrame = window.requestAnimationFrame(() => {
      stickyVisibilityFrame = 0;
      const footer = document.querySelector('.shopify-section--footer');
      const productRegion = document.querySelector('#avontic-primary-product');
      const productRect = productRegion?.getBoundingClientRect();
      const productIsActive = Boolean(productRect && productRect.top < window.innerHeight && productRect.bottom > 0);
      document.querySelectorAll('.av-pdp-sticky-cockpit').forEach((sticky) => {
        const formId = sticky.getAttribute('form');
        const form = formId ? document.getElementById(formId) : null;
        const primaryControl = form?.querySelector('[data-av-atc-control]');
        const purchaseRect = (primaryControl || form)?.getBoundingClientRect();
        const purchaseControlIsVisible = Boolean(
          purchaseRect && purchaseRect.bottom > 0 && purchaseRect.top < window.innerHeight
        );
        const footerOutsideViewport = !footer || footer.getBoundingClientRect().top > window.innerHeight;
        const isRhinoGuardFlow = Boolean(document.querySelector('#avontic-primary-product [data-av-rg-cro-config]'));
        const stickyHeaderBottom = Math.max(0, document.querySelector('.shopify-section--header, .shopify-section-group-header-group')?.getBoundingClientRect().bottom || 0);
        const purchaseHasPassed = Boolean(purchaseRect && purchaseRect.bottom <= stickyHeaderBottom);
        const desktopSticky = window.matchMedia('(min-width: 700px)').matches;
        const purchaseShouldStick = desktopSticky || isRhinoGuardFlow
          ? window.scrollY > 0 && purchaseHasPassed
          : !purchaseControlIsVisible;
        const visible = Boolean(form && purchaseShouldStick && productIsActive && footerOutsideViewport);
        sticky.classList.toggle('is-visible', visible);
        sticky.setAttribute('aria-hidden', visible ? 'false' : 'true');
        sticky.inert = !visible;
      });
      scheduleChatSpace();
    });
  };

  const chatSelectors = [
    'iframe[title*="chat" i]',
    'iframe[src*="shopify-chat" i]',
    '[data-shopify-chat]',
    'shopify-chat',
    '#ShopifyChat',
    '[id*="shopify-chat" i]',
    '[class*="shopify-chat" i]'
  ].join(',');

  const chatTargetSelectors = `${chatSelectors}, [part~="activator"], button[aria-label="Open chat"]`;

  const chatCandidates = () => {
    const candidates = new Set(document.querySelectorAll(chatSelectors));
    const shadowRoots = [];
    const visitedRoots = new Set();

    candidates.forEach((element) => {
      if (element.matches?.('shopify-chat[open]')) return;
      if (element.shadowRoot) shadowRoots.push(element.shadowRoot);
    });

    while (shadowRoots.length) {
      const root = shadowRoots.pop();
      if (!root || visitedRoots.has(root)) continue;
      visitedRoots.add(root);

      root.querySelectorAll('*').forEach((element) => {
        if (element.matches(chatTargetSelectors)) candidates.add(element);
        if (element.shadowRoot) shadowRoots.push(element.shadowRoot);
      });
    }

    return Array.from(candidates);
  };

  const isVisibleChatTarget = (element) => {
    const rect = element.getBoundingClientRect();
    const style = getComputedStyle(element);
    return style.visibility !== 'hidden'
      && style.display !== 'none'
      && Number(style.opacity || 1) > 0
      && rect.width > 0
      && rect.height > 0
      && rect.right > window.innerWidth - 220
      && rect.bottom > window.innerHeight - 220;
  };

  // Inbox replaces its initial shell with a nested runtime after the first
  // conversation opens. Follow both versions and observe only their own roots.
  const inboxObservedRoots = new WeakSet();
  const inboxObserver = new MutationObserver(() => scheduleChatSpace());
  const observeInboxRoot = (root, options) => {
    if (!root || inboxObservedRoots.has(root)) return;
    inboxObservedRoots.add(root);
    inboxObserver.observe(root, options);
  };
  const nativeInboxActivator = () => {
    const inbox = document.querySelector('shopify-chat');
    if (!inbox) return null;
    observeInboxRoot(inbox, { attributes: true, attributeFilter: ['open'] });
    const roots = inbox.shadowRoot ? [inbox.shadowRoot] : [];
    while (roots.length) {
      const root = roots.pop();
      observeInboxRoot(root, { childList: true, subtree: true });
      const activator = root.querySelector('[part~="activator"]');
      if (activator) {
        activator.parentElement?.part.add('av-mobile-help-wrapper');
        // Forward the wrapper part through any nested Inbox component, without
        // changing its existing exported activator or dialog parts.
        let owner = activator.getRootNode();
        while (owner.host && owner.host !== inbox) {
          const host = owner.host;
          const parts = (host.getAttribute('exportparts') || '').split(',').map(part => part.trim()).filter(Boolean);
          if (!parts.includes('av-mobile-help-wrapper')) {
            parts.push('av-mobile-help-wrapper');
            host.setAttribute('exportparts', parts.join(', '));
          }
          owner = host.getRootNode();
        }
        return activator;
      }
      root.querySelectorAll('*').forEach(element => {
        if (element.shadowRoot) roots.push(element.shadowRoot);
      });
    }
    return null;
  };

  let chatReserveFrame = 0;
  const reserveChatSpace = () => {
    const documentElement = document.documentElement;
    const visibleSticky = Array.from(document.querySelectorAll('.av-pdp-sticky-cockpit.is-visible')).find((element) => {
      const rect = element.getBoundingClientRect();
      const style = getComputedStyle(element);
      return style.display !== 'none'
        && style.visibility !== 'hidden'
        && style.pointerEvents !== 'none'
        && rect.width > 0
        && rect.height > 0;
    });
    const mobilePurchaseVisible = window.innerWidth <= 699 && Boolean(visibleSticky);
    documentElement.toggleAttribute('data-av-pdp-mobile-purchase-visible', mobilePurchaseVisible);
    // Reuse Inbox's native activator in the reserved Help slot. Keeping the
    // original control preserves its label, unread indicator and chat behavior.
    const inboxActivator = nativeInboxActivator();
    const dockInbox = mobilePurchaseVisible && Boolean(inboxActivator);
    // Expose only the activator's wrapper so its native nudge animation cannot
    // turn it into an offset containing block while the control is docked.
    if (dockInbox) inboxActivator.parentElement?.part.add('av-mobile-help-wrapper');
    documentElement.toggleAttribute('data-av-inbox-docked', dockInbox);
    if (!mobilePurchaseVisible || dockInbox) {
      documentElement.style.setProperty('--av-pdp-chat-lift', '0px');
      documentElement.removeAttribute('data-av-pdp-chat-lifted');
      return;
    }

    const currentLift = Number.parseFloat(getComputedStyle(documentElement).getPropertyValue('--av-pdp-chat-lift')) || 0;
    const stickyRect = visibleSticky.getBoundingClientRect();
    const baseSticky = {
      top: stickyRect.top + currentLift,
      right: stickyRect.right,
      bottom: stickyRect.bottom + currentLift,
      left: stickyRect.left
    };
    let lift = 0;
    chatCandidates().filter(isVisibleChatTarget).forEach((element) => {
      const rect = element.getBoundingClientRect();
      const isCompactControl = rect.width <= 200 && rect.height <= 200;
      const isInteractive = element.matches('button, [role="button"], iframe');
      if (!isCompactControl || !isInteractive) return;

      const horizontalOverlap = Math.min(baseSticky.right, rect.right) - Math.max(baseSticky.left, rect.left);
      const verticalOverlap = Math.min(baseSticky.bottom, rect.bottom) - Math.max(baseSticky.top, rect.top);
      if (horizontalOverlap <= 0 || verticalOverlap <= 0) return;

      lift = Math.max(lift, Math.ceil(baseSticky.bottom - rect.top + 12));
    });
    lift = Math.min(160, Math.max(0, lift));
    documentElement.style.setProperty('--av-pdp-chat-lift', `${lift}px`);
    documentElement.toggleAttribute('data-av-pdp-chat-lifted', lift > 0);
  };

  const scheduleChatSpace = () => {
    if (chatReserveFrame) return;
    chatReserveFrame = window.requestAnimationFrame(() => {
      chatReserveFrame = 0;
      reserveChatSpace();
    });
  };

  const ready = () => {
    bindStickyPurchaseControls();
    syncStickyVisibility();
    scheduleChatSpace();
    window.setTimeout(scheduleChatSpace, 500);
    window.setTimeout(scheduleChatSpace, 1600);
    window.addEventListener('scroll', syncStickyVisibility, { passive: true });
    window.addEventListener('pageshow', () => {
      syncStickyVisibility();
      window.requestAnimationFrame(syncStickyVisibility);
      window.setTimeout(syncStickyVisibility, 180);
    });
    window.addEventListener('load', () => window.requestAnimationFrame(syncStickyVisibility), { once: true });
    window.addEventListener('resize', () => {
      scheduleChatSpace();
      syncStickyVisibility();
    }, { passive: true });
    const productMutationRoot = document.querySelector('.shopify-section--main-product') || document.querySelector('#avontic-primary-product');
    if (productMutationRoot) new MutationObserver((mutations) => {
      mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) bindStickyPurchaseControls(node);
      }));
      syncStickyVisibility();
    }).observe(productMutationRoot, { childList: true, subtree: true });
    new MutationObserver(scheduleChatSpace).observe(document.body, { childList: true, subtree: true });
    document.addEventListener('shopify:section:load', (event) => {
      if (event.target instanceof Element) bindStickyPurchaseControls(event.target);
      scheduleChatSpace();
      syncStickyVisibility();
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready, { once: true });
  else ready();

  const normalizeAscendStatusCopy = () => {
    if (window.location.pathname !== '/products/titaniumguard-ascend-series-band-case-for-apple-watch') return;
    document.querySelectorAll('[role="status"]').forEach((status) => {
      const current = status.textContent || '';
      const normalized = current
        .replaceAll('Series 11 / 10 / 9 / 8 / 7 45MM', 'Series 9 / 8 / 7 45MM')
        .replaceAll('Series 11 / 10 / 9 / 8 / 7 41MM', 'Series 9 / 8 / 7 41MM');
      if (normalized !== current) status.textContent = normalized;
    });
  };

  const startAscendStatusCopy = () => {
    normalizeAscendStatusCopy();
    const statusRoot = document.querySelector('#avontic-primary-product');
    if (!statusRoot) return;
    new MutationObserver(normalizeAscendStatusCopy).observe(statusRoot, {
      childList: true,
      subtree: true,
      characterData: true
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startAscendStatusCopy, { once: true });
  } else {
    startAscendStatusCopy();
  }

  window.AvonticPdpCore = Object.freeze({ version: CORE_VERSION });
})();
